import * as React from 'react';
import propz from 'propz';
import * as TextMaskCore from 'text-mask-core';
import { Component } from 'react';
import { SchoolEvent } from '../../../../../../../models/event';
import { Button } from '../../../../../../../components/Button/Button';
import './Results.scss';
import {
  convertPointsToString,
  getDefaultDigitMask,
  getIndividualScoreByStudent,
  getIsErrorHashMap,
  getMask,
  getPlayerScoreId,
  getScorePoints,
  getAbbreviationResultByStudent
} from '../../../../../../../helpers/event/schoolEventViewHelper';
import { EMPTY_STRING } from '../../../../../../../consts/date';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, ENTER_KEY } from '../../../../../../../consts/common';

interface Props {
  event: SchoolEvent;
  onSaveClick: (data) => void;
  onCloseClick: () => void;
}

interface State {
  mask: boolean | (string | RegExp)[];
  oldValueHashMap: ValueHashMap;
  valueHashMap: ValueHashMap;
  isErrorHashMap: ErrorHashMap;
  playersCount: number;
  players: Player[];
  abbreviations: Abbreviation[];
  selectedAbbreviations: { [playerId: string]: string };
}

export interface ErrorHashMap {
  [key: string]: boolean;
}

export interface ValueHashMap {
  [key: string]: string;
}

interface Player {
  id: string;
  userId: string;
  permissionId: string;
  firstName: string;
  lastName: string;
}

interface Abbreviation {
  _id: string;
  code: string;
  description: string;
  color: string;
}

export class IndividualInternalResults extends Component<Props, State> {
  inputElement: any[];
  textMaskInputElement: any[];

  constructor(props: Props) {
    super(props);

    this.inputElement = [];
    this.textMaskInputElement = [];

    const { event } = props;
    const mask = propz.get(event, ['sport', 'points', 'inputMask']);

    const inputMask = getMask(mask);

    const { individualsData } = event;

    let valueHashMap = {};
    let oldValueHashMap = {};
    let isErrorHashMap = {};
    let players = [];
    let selectedAbbreviations = {};

    individualsData.forEach(({ id, userId, permissionId, firstName, lastName }) => {
      const points = getIndividualScoreByStudent(event, userId, permissionId);
      const pointsStr = convertPointsToString(event, points);

      const abbreviationResult = getAbbreviationResultByStudent(event, userId, permissionId);
      if (abbreviationResult) {
        selectedAbbreviations[id] = abbreviationResult;
      }

      valueHashMap[id] = points === 0 ? '' : pointsStr;
      oldValueHashMap[id] = points === 0 ? '' : pointsStr;
      isErrorHashMap[id] = false;
      players.push({ id, userId, permissionId, firstName, lastName });
    });

    players = [...players].sort((player1, player2) => {
      const result1 = getIndividualScoreByStudent(event, player1.userId, player1.permissionId);
      const result2 = getIndividualScoreByStudent(event, player2.userId, player2.permissionId);

      if ((result1 === 0 || result1 === null) && (result2 === 0 || result2 === null)) {
        return 1; // Both are zero or null, maintain order
      }
      if (result1 === 0 || result1 === null) return 1; // Move result1 to bottom
      if (result2 === 0 || result2 === null) return -1; // Move result2 to bottom

      return result2 - result1; // Descending order
    });

    this.state = {
      mask: inputMask,
      oldValueHashMap: oldValueHashMap,
      valueHashMap: valueHashMap,
      isErrorHashMap: isErrorHashMap,
      playersCount: individualsData.length,
      players: players,
      abbreviations: this.props.event.sport.abbreviationAvailable
        ? this.props.event.sport.abbreviations.map(abbreviation => ({
            _id: abbreviation._id,
            code: abbreviation.code,
            description: abbreviation.description,
            color: abbreviation.color
          }))
        : [],
      selectedAbbreviations: selectedAbbreviations
    };
  }

  componentDidMount() {
    this.initTextMask();
  }

  initTextMask() {
    const { valueHashMap, playersCount, players, mask } = this.state;
    const { event } = this.props;

    for (let i = 0; i < playersCount; i++) {
      this.textMaskInputElement[i] = TextMaskCore.createTextMaskInputElement({
        inputElement: this.inputElement[i],
        mask: mask || (inputValue => getDefaultDigitMask(event, inputValue)),
        showMask: false
      });
      const { id } = players[i];
      const value = valueHashMap[id];

      this.textMaskInputElement[i].update(value);
    }
  }

  onSubmit = () => {
    const { valueHashMap, players, selectedAbbreviations } = this.state;
    const { event, onSaveClick } = this.props;

    const isErrorHashMap = getIsErrorHashMap(event, valueHashMap, players);
    const isError = players.some(player => isErrorHashMap[player.id] === true);

    if (isError) {
      this.setState({
        isErrorHashMap: isErrorHashMap
      });
      return;
    }

    const scores = players.map(({ userId, permissionId, id }) => {
      const scoreValue = valueHashMap[id] !== '' ? getScorePoints(event, valueHashMap, id) : 0;
      let scoreObject = {
        userId: userId,
        permissionId: permissionId,
        score: scoreValue,
        scoreId: getPlayerScoreId(event, userId, permissionId),
        richScore: { result: scoreValue } as any
      };

      if (selectedAbbreviations[id]) {
        scoreObject.richScore.abbreviationResult = selectedAbbreviations[id];
      } else {
        scoreObject.richScore.abbreviationResult = null;
      }

      return scoreObject;
    });
    onSaveClick(scores);
  };

  onChange(event: React.ChangeEvent<HTMLInputElement>, index: number, id: string) {
    const valueHashMap = { ...this.state.valueHashMap };
    const isErrorHashMap = { ...this.state.isErrorHashMap };
    const value = event.target.value;

    valueHashMap[id] = value;
    isErrorHashMap[id] = false;

    this.setState({
      valueHashMap: valueHashMap,
      isErrorHashMap: isErrorHashMap
    });

    this.textMaskInputElement[index].update(value);
  }

  onKeyDown = (eventDescriptor, index) => {
    const { playersCount } = this.state;

    const isKeyEnter = eventDescriptor.key === ENTER_KEY;
    const isKeyArrowUp = eventDescriptor.key === ARROW_UP_KEY;
    const isKeyArrowDown = eventDescriptor.key === ARROW_DOWN_KEY;
    const isLastInput = index + 1 === playersCount;
    const isFirstInput = index === 0;

    switch (true) {
      case isKeyEnter && !isLastInput:
      case isKeyArrowDown && !isLastInput:
        this.inputElement[index + 1].focus();
        break;
      case isKeyArrowUp && !isFirstInput:
        this.inputElement[index - 1].focus();
        break;
    }
  };

  onFocus(e, index: number, id: string) {
    const value = this.state.valueHashMap[id];
    const oldValueHashMap = { ...this.state.oldValueHashMap };
    oldValueHashMap[id] = value;

    this.setState({
      oldValueHashMap: oldValueHashMap
    });

    this.textMaskInputElement[index].update('');

    e.stopPropagation();
  }

  onBlur(e, index: number, id: string) {
    const eventValue = e.target.value;
    const oldValueHashMap = { ...this.state.oldValueHashMap };

    if (eventValue === EMPTY_STRING) {
      const valueHashMapWithOldValue = { ...this.state.valueHashMap };
      valueHashMapWithOldValue[id] = oldValueHashMap[id];

      this.setState({
        valueHashMap: valueHashMapWithOldValue
      });
      this.textMaskInputElement[index].update(oldValueHashMap[id]);
    } else {
      const valueHashMap = { ...this.state.valueHashMap };
      valueHashMap[id] = eventValue;

      this.setState({
        valueHashMap: valueHashMap
      });
      this.textMaskInputElement[index].update(eventValue);
    }

    e.stopPropagation();
  }

  renderInput(index: number, id: string): React.ReactNode {
    const { isErrorHashMap } = this.state;
    return (
      <input
        ref={el => (this.inputElement[index] = el)}
        onKeyDown={event => this.onKeyDown(event, index)}
        onChange={event => this.onChange(event, index, id)}
        onFocus={event => this.onFocus(event, index, id)}
        onBlur={event => this.onBlur(event, index, id)}
        style={{ maxWidth: '80px' }}
        className={
          isErrorHashMap[id] === true ? 'eIndividualInternalResultsInput mError' : 'eIndividualInternalResultsInput'
        }
      />
    );
  }

  renderAbbreviationCheckboxes(playerId: string) {
    return (
      <div className="abbreviation-checkboxes-container">
        {this.state.abbreviations.map((abbreviation, index) => (
          <label key={index} className="abbreviation-checkbox-label">
            <input
              type="checkbox"
              className="abbreviation-checkbox"
              id={`${abbreviation.code}_${playerId}`}
              name="abbreviation"
              checked={this.state.selectedAbbreviations[playerId] === abbreviation.code}
              onChange={e => this.handleAbbreviationChange(playerId, abbreviation.code, e.target.checked)}
            />
            {abbreviation.code}
          </label>
        ))}
      </div>
    );
  }

  handleAbbreviationChange(playerId: string, code: string, isChecked: boolean) {
    if (isChecked) {
      this.setState(prevState => ({
        selectedAbbreviations: {
          ...prevState.selectedAbbreviations,
          [playerId]: code
        }
      }));
    } else {
      const selectedAbbreviations = { ...this.state.selectedAbbreviations };
      delete selectedAbbreviations[playerId];
      this.setState({ selectedAbbreviations });
    }
  }

  render() {
    const { players, abbreviations } = this.state;
    const { onCloseClick } = this.props;

    const isPlayersExist = players.length > 0;
    const hasAbbreviations = abbreviations.length > 0;

    if (!isPlayersExist) {
      return (
        <>
          <h1>Error</h1>
          <div className="alert alert-danger">First, add players in event</div>
          <Button text={'Close'} onClick={onCloseClick} customClass={'mt-3 mb-3 mr-3 btn-secondary'} />
        </>
      );
    }

    return (
      <div style={{ width: '100%' }}>
        <h2>Participant results</h2>
        <div>
          <table className={''}>
            <thead>
              <tr>
                <th className={'eIndividualInternalResultsCell'} style={{ width: '5%' }}>
                  #
                </th>
                <th className={'eIndividualInternalResultsCell'} style={{ width: hasAbbreviations ? '35%' : '55%' }}>
                  Player
                </th>
                <th className={'eIndividualInternalResultsCell'} style={{ width: hasAbbreviations ? '15%' : '35%' }}>
                  Results
                </th>
                {hasAbbreviations && <th className={'eIndividualInternalResultsCell'} style={{ width: '25%' }}></th>}
              </tr>
            </thead>
            <tbody>
              {players.map(({ firstName, lastName, id }, index) => (
                <tr key={`individual_internal_results_${index}`}>
                  <td className={'eIndividualInternalResultsCell'} style={{ width: '5%' }}>
                    {index + 1}.
                  </td>
                  <td className={'eIndividualInternalResultsCell'} style={{ width: hasAbbreviations ? '35%' : '55%' }}>
                    {`${firstName} ${lastName}`}
                  </td>
                  <td className={'eIndividualInternalResultsCell'} style={{ width: hasAbbreviations ? '15%' : '35%' }}>
                    {this.renderInput(index, id)}
                  </td>
                  {hasAbbreviations && (
                    <td className={'eIndividualInternalResultsCell'} style={{ width: '25%' }}>
                      {this.renderAbbreviationCheckboxes(id)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="eResultsActions">
            <Button text={'Cancel'} onClick={onCloseClick} customClass={'btn-secondary'} />
            <Button text={'Save results'} onClick={this.onSubmit} />
          </div>
        </div>
      </div>
    );
  }
}
